import { createFileRoute } from "@tanstack/react-router";
import { PageSection } from "@/components/site/PageSection";
import { LazyMapFrame } from "@/components/site/LazyMapFrame";
import { EsPageShell } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema } from "@/lib/es-pages";
import { EsContactForm } from "@/components/site/EsContactForm";

const ES_TOPIC_PREFILL: Record<string, string> = {
  verificacion:
    "Por favor, verifiquen si tengo cobertura para cirugía bariátrica.",
  "self-pay-callback":
    "Me gustaría que Val o Jamie me llamen para hablar de la opción de pago propio.",
};

export const Route = createFileRoute("/es/contacto")({
  validateSearch: (search: Record<string, unknown>): { topic?: string } =>
    typeof search["topic"] === "string" ? { topic: search["topic"] } : {},
  head: ({ match }) => {
    const base = esHead("contact");
    // Filtered variants like /es/contacto?topic=verificacion are the same page
    // with a prefilled message; they must not be indexed (mirrors /contact).
    const isFiltered = Boolean((match.search as { topic?: string } | undefined)?.topic);
    return {
      ...base,
      meta: [...base.meta, ...(isFiltered ? [{ name: "robots", content: "noindex,follow" }] : [])],
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(esWebPageSchema("contact")),
            },
          ]
        : [],
    };
  },
  component: EsContact,
});

function EsContact() {
  const { topic } = Route.useSearch();
  const prefillMessage = topic ? (ES_TOPIC_PREFILL[topic] ?? "") : "";
  return (
    <EsPageShell
      page="contact"
      title="Contáctenos"
      intro="Llame, escriba un correo o use el formulario para pedir una cita o hacer una pregunta. Le respondemos lo antes posible."
    >
      <PageSection className="pb-6 grid gap-3 md:grid-cols-2">
        <div className="flex h-full flex-col gap-3">
          <aside className="flex">
            <div className="flex-1 rounded-2xl bg-brand-soft/50 p-5 md:p-6">
              <h2 className="font-serif text-2xl text-brand-deep">Dirección del consultorio</h2>
              <address className="not-italic mt-2 text-[0.9rem] leading-[1.4] text-foreground/80">
                Virginia Bariatric Surgery
                <br />
                171 Elden Street, Suite 3B
                <br />
                Herndon, VA 20170
              </address>
              <p className="mt-3 text-sm text-muted-foreground">
                Horario: lunes a viernes, de 8:00 a. m. a 4:30 p. m.
              </p>
            </div>
          </aside>

          <aside className="flex">
            <dl className="flex-1 divide-y divide-border rounded-2xl border border-border bg-card">
              <div className="flex items-baseline justify-between gap-4 p-4">
                <dt className="text-sm uppercase tracking-wider text-muted-foreground">
                  Teléfono
                </dt>
                <dd className="text-[0.9rem]">
                  <a
                    className="font-medium text-brand-logo"
                    aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771"
                    href="tel:7037099771"
                  >
                    (703) 709-9771
                  </a>
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 p-4">
                <dt className="text-sm uppercase tracking-wider text-muted-foreground">Fax</dt>
                <dd className="text-[0.9rem] text-foreground/80">(888) 519-0045</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 p-4">
                <dt className="text-sm uppercase tracking-wider text-muted-foreground">
                  Correo electrónico
                </dt>
                <dd className="text-[0.9rem]">
                  <a
                    className="break-words font-medium text-brand-logo"
                    href="mailto:Val@virginiabariatric.com"
                  >
                    Val@virginiabariatric.com
                  </a>
                </dd>
              </div>
            </dl>
          </aside>
        </div>

        <EsContactForm prefillMessage={prefillMessage} />
      </PageSection>

      <PageSection className="pb-6">
        <h2 className="text-center font-serif text-2xl text-brand-deep">
          Cómo llegar a nuestro consultorio en Herndon
        </h2>
        <div className="mt-4 overflow-hidden rounded-2xl border border-border">
          <LazyMapFrame
            title="Mapa de Google que muestra Virginia Bariatric Surgery en 171 Elden Street, Suite 3B, Herndon, VA 20170"
            src="https://www.google.com/maps?q=171%20Elden%20Street%20Suite%203B%2C%20Herndon%2C%20VA%2020170&output=embed"
          />
        </div>
      </PageSection>
    </EsPageShell>
  );
}
